/**
 * Government Tab — FHA / VA / USDA specific fields.
 * Shows editable fields (refinance type, streamline type, etc.) and
 * read-only computed fields (MIP, Funding Fee, Guarantee Fee).
 * Only renders for government mortgage types.
 */
import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import EditableField from '../../../components/EditableField';
import SectionHeader from '../../../components/shared/SectionHeader';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';

import { fmtDollar as fmt, fmtNum as num, fmtDate as date } from '../../../utils/formatters';
import { isRefinanceLoan } from '../../../utils/loanPurposeDisplay';

import DataItem from '../../../components/shared/DataItem';

const BoolItem = ({ label, value }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">
      {value === true ? 'Yes' : value === false ? 'No' : '—'}
    </Typography>
  </Box>
);

// ── Option lists ──

const FHA_REFINANCE_TYPES = [
  { value: 'Simple', label: 'Simple' },
  { value: 'Streamline', label: 'Streamline' },
  { value: 'RateTerm', label: 'Rate & Term' },
];

const FHA_STREAMLINE_TYPES = [
  { value: 'CreditQualifying', label: 'Credit Qualifying' },
  { value: 'NonCreditQualifying', label: 'Non-Credit Qualifying' },
];

const VA_REFINANCE_TYPES = [
  { value: 'InterestRateReductionRefinanceLoan', label: 'IRRRL' },
  { value: 'Type1CashOut', label: 'Type 1 Cash-Out' },
  { value: 'Type2CashOut', label: 'Type 2 Cash-Out' },
];

const USDA_REFINANCE_TYPES = [
  { value: 'NonStreamlined', label: 'Non-Streamlined' },
  { value: 'Streamlined', label: 'Streamlined' },
  { value: 'StreamlinedAssist', label: 'Streamlined-Assist' },
];

// ══════════════════════════════════════════════════════════════════
//  FHA Section
// ══════════════════════════════════════════════════════════════════

const FhaSection = ({ fha, namePath, isEditMode, isRefinance }) => {
  return (
    <Grid container spacing={2}>
      {/* Editable — Refinance Fields (only for refinance loans) */}
      {isRefinance && (
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <SectionHeader title="FHA Refinance" icon={<GavelIcon fontSize="small" />} />
              <Grid container spacing={isEditMode ? 2 : 1}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <EditableField
                    label="FHA Refinance Type"
                    value={fha.fhaRefinanceType}
                    name={`${namePath}.fha.fhaRefinanceType`}
                    isEditMode={isEditMode}
                    options={isEditMode ? FHA_REFINANCE_TYPES : null}
                  />
                </Grid>
                {(fha.fhaRefinanceType === 'Streamline' || isEditMode) && (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <EditableField
                      label="Streamline Type"
                      value={fha.fhaStreamlineRefinanceType}
                      name={`${namePath}.fha.fhaStreamlineRefinanceType`}
                      isEditMode={isEditMode}
                      options={isEditMode ? FHA_STREAMLINE_TYPES : null}
                    />
                  </Grid>
                )}
                {fha.maxStreamlineBaseLoanAmount != null && (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <DataItem label="Max Streamline Base Loan">{fmt(fha.maxStreamlineBaseLoanAmount)}</DataItem>
                  </Grid>
                )}
                {fha.maxStreamlineTotalLoanAmount != null && (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <DataItem label="Max Streamline Total Loan">{fmt(fha.maxStreamlineTotalLoanAmount)}</DataItem>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Read-only — Case Info */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="FHA Case" icon={<AccountBalanceIcon fontSize="small" />} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="FHA Case Number">{fha.fhaAgencyCaseNumber || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Case Assignment Date">{date(fha.fhaCaseAssignmentDate)}</DataItem>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Read-only — MIP */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="Mortgage Insurance Premium (MIP)" icon={<PaymentsIcon fontSize="small" />} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Upfront MIP">{fmt(fha.upfrontMipAmount)}</DataItem>
              </Grid>
              {fha.upfrontMipRefundAmount != null && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Upfront MIP Refund">{fmt(fha.upfrontMipRefundAmount)}</DataItem>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Monthly MIP">{fmt(fha.monthlyMipAmount)}</DataItem>
              </Grid>
              {fha.monthlyMipEstimate != null && fha.monthlyMipAmount == null && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Monthly MIP (Estimate)">{fmt(fha.monthlyMipEstimate)}</DataItem>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="MIP Term">{fha.monthlyMipTermMonths != null ? `${fha.monthlyMipTermMonths} months` : '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="MIP Financed"
                  value={fha.upfrontMipFinanced === true ? 'Yes' : fha.upfrontMipFinanced === false ? 'No' : (fha.upfrontMipFinanced || '')}
                  name={`${namePath}.fha.upfrontMipFinanced`}
                  isEditMode={isEditMode}
                  options={isEditMode ? [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] : null}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ══════════════════════════════════════════════════════════════════
//  VA Section
// ══════════════════════════════════════════════════════════════════

const VaSection = ({ va, namePath, isEditMode, isRefinance }) => {

  const isIrrrl = va.vaRefinanceType === 'InterestRateReductionRefinanceLoan';

  return (
    <Grid container spacing={2}>
      {/* Editable — Refinance Fields */}
      {isRefinance && (
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <SectionHeader title="VA Refinance" icon={<GavelIcon fontSize="small" />} />
              <Grid container spacing={isEditMode ? 2 : 1}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <EditableField
                    label="VA Refinance Type"
                    value={va.vaRefinanceType}
                    name={`${namePath}.va.vaRefinanceType`}
                    isEditMode={isEditMode}
                    options={isEditMode ? VA_REFINANCE_TYPES : null}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Read-only — Case */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="VA Case" icon={<AccountBalanceIcon fontSize="small" />} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="VA Case Number">{va.vaAgencyCaseNumber || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <BoolItem label="Funding Fee Exempt" value={va.vaFundingFeeExempt} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Read-only — Funding Fee */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="VA Funding Fee" icon={<PaymentsIcon fontSize="small" />} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Upfront Funding Fee">{fmt(va.upfrontVaFundingFee)}</DataItem>
              </Grid>
              {va.upfrontVaFundingFeeEstimate != null && va.upfrontVaFundingFee == null && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Funding Fee (Estimate)">{fmt(va.upfrontVaFundingFeeEstimate)}</DataItem>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Funding Fee Financed"
                  value={va.vaFundingFeeFinanced === true ? 'Yes' : va.vaFundingFeeFinanced === false ? 'No' : (va.vaFundingFeeFinanced || '')}
                  name={`${namePath}.va.vaFundingFeeFinanced`}
                  isEditMode={isEditMode}
                  options={isEditMode ? [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] : null}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Read-only — IRRRL (only when refi type is IRRRL) */}
      {isIrrrl && (
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <SectionHeader title="IRRRL Details" icon={<HomeIcon fontSize="small" />} />
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Max IRRRL Base Loan">{fmt(va.maxIrrrlBaseLoanAmount)}</DataItem>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Max IRRRL Total Loan">{fmt(va.maxIrrrlTotalLoanAmount)}</DataItem>
                </Grid>
                {va.vaIrrrlRecoupmentPeriodMonths != null && (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <DataItem label="Recoupment Period">{num(va.vaIrrrlRecoupmentPeriodMonths)} months</DataItem>
                  </Grid>
                )}
                {va.vaIrrrlAmountExceedingRecoupment != null && (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <DataItem label="Amount Exceeding Recoupment">{fmt(va.vaIrrrlAmountExceedingRecoupment)}</DataItem>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Read-only — Residual Income */}
      {va.vaResidualIncomeGuideline != null && (
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <SectionHeader title="Residual Income" icon={<PaymentsIcon fontSize="small" />} />
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Residual Income Guideline">{fmt(va.vaResidualIncomeGuideline)}</DataItem>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// ══════════════════════════════════════════════════════════════════
//  USDA Section
// ══════════════════════════════════════════════════════════════════

const UsdaSection = ({ usda, namePath, isEditMode, isRefinance }) => {
  return (
    <Grid container spacing={2}>
      {/* Editable Fields */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="USDA Loan Details" icon={<GavelIcon fontSize="small" />} />
            <Grid container spacing={isEditMode ? 2 : 1}>
              {isRefinance && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <EditableField
                    label="USDA Refinance Type"
                    value={usda.usdaRefinanceType}
                    name={`${namePath}.usda.usdaRefinanceType`}
                    isEditMode={isEditMode}
                    options={isEditMode ? USDA_REFINANCE_TYPES : null}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Guarantee Fee Financed"
                  value={usda.usdaGuaranteeFeeFinanced === true ? 'Yes' : usda.usdaGuaranteeFeeFinanced === false ? 'No' : (usda.usdaGuaranteeFeeFinanced || '')}
                  name={`${namePath}.usda.usdaGuaranteeFeeFinanced`}
                  isEditMode={isEditMode}
                  options={isEditMode ? [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] : null}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Household Size"
                  value={usda.householdSize}
                  name={`${namePath}.usda.householdSize`}
                  isEditMode={isEditMode}
                  type={isEditMode ? 'number' : 'text'}
                  placeholder="4"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <EditableField
                  label="Household Annual Income"
                  value={usda.householdAnnualIncome}
                  name={`${namePath}.usda.householdAnnualIncome`}
                  isEditMode={isEditMode}
                  type="currency"
                  placeholder="85,000"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Read-only — Guarantee Fee */}
      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <SectionHeader title="USDA Guarantee Fee" icon={<PaymentsIcon fontSize="small" />} />
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Upfront Guarantee Fee">{fmt(usda.upfrontGuaranteeFee)}</DataItem>
              </Grid>
              {usda.upfrontGuaranteeFeeEstimate != null && usda.upfrontGuaranteeFee == null && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Guarantee Fee (Estimate)">{fmt(usda.upfrontGuaranteeFeeEstimate)}</DataItem>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <DataItem label="Monthly Guarantee Fee">{fmt(usda.guaranteeFeeMonthlyPayment)}</DataItem>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Read-only — Income */}
      {usda.adjustedHouseholdAnnualIncome != null && (
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <SectionHeader title="USDA Household Income" icon={<PaymentsIcon fontSize="small" />} />
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <DataItem label="Adjusted Household Annual Income">{fmt(usda.adjustedHouseholdAnnualIncome)}</DataItem>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// ══════════════════════════════════════════════════════════════════
//  Main GovernmentTab
// ══════════════════════════════════════════════════════════════════

const GovernmentTab = ({ loanDetail, isEditMode = false }) => {
  if (!loanDetail) return null;

  const loan = loanDetail.loan || {};
  const govt = loanDetail.governmentLoanInfo;
  if (!govt) return null;

  const mortgageType = loan.mortgageType;
  const isRefinance = isRefinanceLoan(loan);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip label={mortgageType} color="primary" size="small" />
        <Typography variant="body2" color="text.secondary">
          Government loan fields — editable fields will be saved with your loan changes
        </Typography>
      </Stack>

      {mortgageType === 'FHA' && (
        <FhaSection fha={govt.fha || {}} namePath="governmentLoanInfo" isEditMode={isEditMode} isRefinance={isRefinance} />
      )}
      {mortgageType === 'VA' && (
        <VaSection va={govt.va || {}} namePath="governmentLoanInfo" isEditMode={isEditMode} isRefinance={isRefinance} />
      )}
      {mortgageType === 'USDA' && (
        <UsdaSection usda={govt.usda || {}} namePath="governmentLoanInfo" isEditMode={isEditMode} isRefinance={isRefinance} />
      )}
    </Box>
  );
};

export default GovernmentTab;
